import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchRoleBasedProjects } from '@/lib/fetchRoleBasedProjects';
import { primeSidebarProjectsCache } from '@/lib/sidebarProjectsCache';

export interface AssignedProject {
  id: string;
  project_code: string;
  project_name: string;
  site_address: string | null;
  status: string;
  cover_image: string | null;
  current_stage: string | null;
  progress: number;
  client_name: string | null;
}

function isScheduleItemComplete(item: {
  is_complete: boolean;
  progress: number;
  actual_end_date: string | null;
}): boolean {
  if (item.is_complete) return true;
  if (item.progress >= 100) return true;
  if (item.actual_end_date != null && item.actual_end_date !== '') return true;
  return false;
}

export interface UseSubcontractorAssignmentsResult {
  projects: AssignedProject[];
  projectIds: string[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const SUBCONTRACTOR_ASSIGNMENT_COLUMNS =
  'id, project_code, project_name, site_address, status, cover_image, cover_photo_url, client_name, created_at, updated_at, project_progress, current_stage';

export function useSubcontractorAssignments(): UseSubcontractorAssignmentsResult {
  const [projects, setProjects] = useState<AssignedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchRoleBasedProjects({
        columns: SUBCONTRACTOR_ASSIGNMENT_COLUMNS,
      });

      if (import.meta.env.DEV) {
        console.log('[useSubcontractorAssignments] fetchRoleBasedProjects result:', {
          role: result.role,
          profileId: result.profileId,
          projectCount: result.rows.length,
          error: result.error,
        });
      }

      if (result.error) {
        setError(result.error);
        setProjects([]);
        setLoading(false);
        return;
      }

      if (result.role !== 'subcontractor') {
        setError('Current user is not a subcontractor');
        setProjects([]);
        setLoading(false);
        return;
      }

      const rawProjects = result.rows as Array<{
        id: string;
        project_code: string;
        project_name: string;
        site_address: string | null;
        status: string;
        cover_image: string | null;
        cover_photo_url: string | null;
        client_name: string | null;
        created_at: string;
        updated_at: string;
        project_progress: number | null;
        current_stage: string | null;
      }>;

      const assignedProjectIds = rawProjects.map((p) => p.id);

      if (assignedProjectIds.length === 0) {
        setProjects([]);
        setLoading(false);
        return;
      }

      // Load REAL schedule items for all assigned projects (source of truth)
      const { data: scheduleRows, error: scheduleErr } = await supabase
        .from('jphph_project_schedule_items')
        .select(
          'id, project_id, is_complete, progress, main_stage, phase, is_included, actual_end_date'
        )
        .in('project_id', assignedProjectIds)
        .eq('is_included', true);

      if (import.meta.env.DEV) {
        console.log('[useSubcontractorAssignments] loaded schedule items:', {
          count: scheduleRows?.length ?? 0,
          error: scheduleErr?.message ?? null,
        });
      }

      if (scheduleErr) {
        console.error('[useSubcontractorAssignments] schedule fetch error:', scheduleErr);
      }

      // Group schedule items by project
      const scheduleByProject = new Map<string, typeof scheduleRows>();
      for (const row of scheduleRows || []) {
        const arr = scheduleByProject.get(row.project_id) || [];
        arr.push(row);
        scheduleByProject.set(row.project_id, arr);
      }

      // Derive stage and progress from real schedule data
      const mapped: AssignedProject[] = rawProjects.map((p) => {
        const items = scheduleByProject.get(p.id) || [];

        // Compute progress: completed / total schedule items
        const total = items.length;
        const completed = items.filter(isScheduleItemComplete).length;
        const liveProgress = total > 0 ? Math.round((completed / total) * 100) : 0;

        // Determine active stage from real schedule items — use actual stage names in data order
        const uniqueStages = Array.from(new Set(items.map((si) => si.main_stage || si.phase || 'General')));
        // Sort stages by the earliest sort_order of items within each stage
        const stagesWithOrder = uniqueStages.map((name) => {
          const stageItems = items.filter((si) => (si.main_stage || si.phase || 'General') === name);
          const minSort = Math.min(...stageItems.map((si) => si.sort_order ?? 999));
          const allComplete = stageItems.every(isScheduleItemComplete);
          return { name, minSort, allComplete, count: stageItems.length };
        }).sort((a, b) => a.minSort - b.minSort);

        let activeStage = stagesWithOrder.length > 0 ? stagesWithOrder[stagesWithOrder.length - 1].name : 'General';
        for (const s of stagesWithOrder) {
          if (s.count > 0 && !s.allComplete) {
            activeStage = s.name;
            break;
          }
        }
        if (liveProgress === 100) activeStage = 'Completed';

        // Prefer stored DB values if fresh (within 1 hour)
        const updatedAtMs = p.updated_at ? new Date(p.updated_at).getTime() : 0;
        // Use 5 minute freshness window so subcontractor sees updated progress quickly
        const fiveMinutesAgo = Date.now() - 300_000;
        const hasFreshStored =
          p.project_progress !== null && p.current_stage !== null && updatedAtMs > fiveMinutesAgo;

        const displayProgress = hasFreshStored ? (p.project_progress ?? 0) : liveProgress;
        const displayStage = hasFreshStored ? (p.current_stage || 'Planning') : activeStage;

        if (import.meta.env.DEV) {
          console.log('[useSubcontractorAssignments] project computed:', {
            projectId: p.id,
            name: p.project_name,
            liveProgress,
            liveStage: activeStage,
            storedProgress: p.project_progress,
            storedStage: p.current_stage,
            hasFreshStored,
            displayProgress,
            displayStage,
            scheduleItems: total,
            completedItems: completed,
          });
        }

        return {
          id: p.id,
          project_code: p.project_code,
          project_name: p.project_name,
          site_address: p.site_address,
          status: p.status,
          cover_image: p.cover_photo_url || p.cover_image,
          current_stage: displayStage,
          progress: displayProgress,
          client_name: p.client_name,
        };
      });

      setProjects(mapped);

      if (result.profileId && result.role) {
        primeSidebarProjectsCache(
          result.profileId,
          result.role,
          mapped.map((p) => ({ id: p.id, project_name: p.project_name })),
        );
      }

      if (import.meta.env.DEV) {
        console.log('[useSubcontractorAssignments] final mapped projects:', mapped.map((m) => ({
          id: m.id,
          name: m.project_name,
          stage: m.current_stage,
          progress: m.progress,
        })));
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Re-fetch when schedule/checklist sync events fire (e.g. after stage completes)
  useEffect(() => {
    const handler = () => {
      // Invalidate cached data by re-running load
      load();
    };
    window.addEventListener('__jphph_schedule_sync__', handler);
    return () => window.removeEventListener('__jphph_schedule_sync__', handler);
  }, [load]);

  // Re-fetch when page becomes visible again
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [load]);

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);

  return {
    projects,
    projectIds,
    loading,
    error,
    refetch: load,
  };
}